import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  Home as HomeIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import landingBackground from '../images/landingbackground3.jpg';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const handleHome = () => {
    navigate('/');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      maxWidth: '1200px', 
      mx: 'auto',
      overflowY: 'auto' // Ensure scrolling works
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '40vh', md: '50vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            My Profile
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            Welcome back to Create A Dish
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ 
        p: { xs: 2, md: 4 }, 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        pb: { xs: 10, md: 6 } // Add bottom padding to account for fixed bottom navigation
      }}>
        <Card sx={{ maxWidth: 600, width: '100%', mb: 4 }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Avatar
              src={user?.picture}
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 2,
                bgcolor: 'primary.main',
                fontSize: '2.5rem'
              }}
            >
              {user?.name ? user.name.charAt(0).toUpperCase() : <PersonIcon fontSize="large" />}
            </Avatar>

            <Typography
              variant="h4"
              component="h2"
              sx={{
                mb: 2,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              {user?.name || 'User'}
            </Typography>

            {user?.email && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {user.email}
              </Typography>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Thank you for being part of the Create A Dish community!
            </Typography>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            maxWidth: 400,
            mt: 2,
            mb: 4, // Add margin bottom for extra spacing
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={handleHome}
            sx={{
              minWidth: 150,
              fontSize: '1.1rem',
              py: 1.5,
            }}
          >
            Home
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              minWidth: 150,
              fontSize: '1.1rem',
              py: 1.5,
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                backgroundColor: 'error.light',
                color: 'error.dark',
              },
            }}
          >
            Log Out
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Profile;